import type {Observable, Subject} from 'rxjs';
import {BehaviorSubject} from 'rxjs';
import Autocomplete from '@trevoreyre/autocomplete';
import {uuid as generateUuid} from '../../utils/custom-utils';
import {debounce} from 'lodash-es';
import {assertIsHtmlInputElement} from 'shared/utils/types-utils';

export const AUTOCOMPLETE_INPUT_EVENT = 'autocomplete-input';
export const AUTOCOMPLETE_HINT_SELECTED_EVENT = 'autocomplete-hint-select';

// fixme missing tests
export function parseHighlightedQueryText(option: string, query: string): [prefix: string, highlighted: string, suffix: string] {
    const normalizedOptions = normalizeText(option);
    const normalizedQuery = normalizeText(query ?? ''); // query can be null
    const startIndex = normalizedOptions.indexOf(normalizedQuery);
    const endIndex = startIndex + normalizedQuery.length
    const prefix = option.slice(0, startIndex);
    const highlighted = option.slice(startIndex, endIndex);
    const suffix = option.slice(endIndex);
    return [prefix, highlighted, suffix];
}

// fixme missing tests
export function containsQueryText(option: string, query: string): boolean {
    return normalizeText(option).indexOf(normalizeText(query ?? '')) !== -1; // query can be null
}

function normalizeText(text: string) {
    // TODO (optional) also remove diacritics
    return text.toLowerCase();
}

export interface AutocompleteState<ITEM = string> {
    expanded: boolean,
    selectedIndex: number;
    inputValue: string;
    results: ITEM[];
    loading: boolean;
}

export class AutocompleteController<ITEM = string> {
    private state: AutocompleteState;
    private readonly state$: Subject<AutocompleteState>;
    public readonly resultListId: string;
    public readonly core: Autocomplete;

    constructor(search: (_: string) => ITEM[] | Promise<ITEM[]>,
                initialValue: string,
                debounceTime = 0,
                // eslint-disable-next-line @typescript-eslint/no-base-to-string
                public getResultValue: (_: ITEM) => string = (result) => result.toString()) {
        this.state = {
            expanded: false,
            inputValue: initialValue,
            loading: false,
            results: [],
            selectedIndex: -1
        }

        this.state$ = new BehaviorSubject(this.state);

        this.resultListId = `autocomplete-result-list-${generateUuid()}`;

        this.core = new Autocomplete({
            search,
            autoSelect: false,
            setValue: this.setValue.bind(this),
            onUpdate: this.handleUpdate.bind(this),
            onShow: this.handleShow.bind(this),
            onHide: this.handleHide.bind(this),
            onLoading: this.handleLoading.bind(this),
            onLoaded: this.handleLoaded.bind(this)
        });

        if (debounceTime > 0) {
            this.core.handleInput = debounce(this.core.handleInput, debounceTime)
        }
    }

    public updateCurrentInputValue(value: string) {
        this.state = {...this.state, inputValue: value};
    }

    public getState$(): Observable<AutocompleteState> {
        return this.state$.asObservable();
    }

    public setValue(result: ITEM | undefined = undefined) {
        const inputValue = result ? this.getResultValue(result) : '';
        this.state = {...this.state, inputValue};
        this.state$.next(this.state);
    }

    public handleUpdate(updatedResults: string[], updatedSelectedIndex: number) {
        const results = updatedResults;
        const selectedIndex = updatedSelectedIndex;
        this.state = {...this.state, results, selectedIndex};
        this.state$.next(this.state);
    }

    public handleShow() {
        this.state.expanded = true;
        this.state$.next(this.state);
    }

    public handleHide() {
        this.state.expanded = false;
        this.state$.next(this.state);
    }

    public handleLoading() {
        this.state.loading = true;
        this.state$.next(this.state);
    }

    public handleLoaded() {
        this.state.loading = false;
        this.state$.next(this.state);
    }

    public handleInput(event: Event) {
        assertIsHtmlInputElement(event.target)
        const inputValue = event.target.value;
        this.core.handleInput(event)
        this.state = {...this.state, inputValue};
        this.state$.next(this.state);
    }
}